import styled from 'styled-components';
import { Heading3 } from '../Typography/Heading';
import RadioButton from '../RadioButton/RadioButton';
import { windowMaxWidth } from './breakpoints';

// Navbar Styles
const OnboardingNavbarWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  height: 60px;
  justify-content: space-between;
  margin-bottom: 70px;
  padding: 10px 30px;
  width: 100%;

  @media ${windowMaxWidth.laptopM} {
    padding: 10px;
  }
`;

// Progress Styles
const OnboardingProgressStepsWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
`;

// Header Styles
const OnboardingHeaderGroup = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  padding: 40px;

  &.no-border {
    border-bottom: none;
    padding: 40px 40px 20px;
  }

  &.step-locked {
    border-bottom: none;
    padding: 24px 40px 0;

    h3 {
      margin: 0;
    }
  }
`;

// Content Styles
const OnboardingPageWrapper = styled.div`
  align-items: center;
  background: var(--secondary-whisper);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  min-height: 100vh;

  @media ${windowMaxWidth.laptopM} {
    padding: 0 20px;
  }
`;

const OnboardingContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: auto;
  margin-top: 50px;
  max-width: 1200px;
  padding: 10px;
  width: 100%;

  &.step-locked {
    box-sizing: border-box;
    flex: 1 1 auto;
    justify-content: center;
    margin-top: 0;
    max-width: 600px;
    min-height: calc(100vh - 150px);

    @supports (height: 100dvh) {
      min-height: calc(100dvh - 150px);
    }
  }

  &.step-initial {
    max-width: 712px;
  }

  &.step-overview {
    max-width: 926px;
  }

  &.step-organization {
    max-width: 742px;
  }

  &.step-housing-stock {
    max-width: 742px;
  }
`;

const OnboardingContentGroup = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  padding: 20px 40px;

  form {
    width: 100%;
  }

  &.no-border {
    border-bottom: none;
  }

  h3 {
    margin-top: 0;
  }

  &.step-locked {
    border: none;
    text-align: center;
    padding: 8px 40px;
  }

  &.step-overview {
    &.content {
      padding: 20px 40px 40px;
      gap: 10px;
    }
  }

  &.step-organization {
    border-bottom: none;
    padding-bottom: 20px;
  }

  &.step-appearance {
    .ant-form-item {
      margin-bottom: 0;
    }

    > div {
      width: 100%;
    }

    &.logo {
      padding: 20px 40px 10px;

      > div {
        margin-bottom: 0;
      }

      ul {
        margin-bottom: 10px !important;
      }
    }

    &.title {
      align-items: flex-start;
    }

    &.imprint {
      border: none;
      padding: 20px 40px 30px;

      > div {
        margin-bottom: 0;
      }

      ul {
        margin-bottom: 10px !important;
      }
    }
  }

  &.step-users {
    &.action {
      align-items: center;
      border-bottom: none;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;

      h3 {
        line-height: 26px;
        margin: 0;
      }
    }

    &.content-head {
      padding: 0 60px 10px 40px;
    }

    &.content {
      border: none;
      padding: 0 20px 20px;
    }
  }

  &.step-organizations {
    &.action {
      align-items: flex-start;
      border-bottom: none;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-between;

      h3 {
        line-height: 26px;
        margin: 0;
      }
    }

    &.content-head {
      padding: 0 60px 10px 40px;

      &.empty {
        padding-bottom: 40px;
        border-bottom: none;
      }
    }

    &.content {
      border: none;
      padding: 0 20px 20px;
    }
  }

  &.step-housing-stock {
    &.content {
      padding: 20px 40px 40px;
      gap: 4px;

      div {
        width: 100%;
      }
    }
  }

  &.step-notifications {
    &:last-of-type {
      padding-bottom: 40px;
    }
  }

  &.step-interface {
    &.content {
      padding: 20px 40px 40px;
    }
  }

  &.step-api {
    align-items: flex-start;
    padding: 20px 40px 40px;
  }

  &.step-organization-users {
    &.content {
      padding: 0 20px;
      border: none;
    }
  }

  &.step-outro {
    &.footer {
      align-items: center;
      border-bottom: none;
      display: flex;
      flex-flow: row wrap;
      gap: 20px;
      justify-content: space-between;
      margin: auto;
      max-width: 660px;
      padding: 20px 0 40px;
      width: 100%;
    }
  }

  &.step-import-upload {
    &.content {
      padding: 0 0 20px;
    }
  }
`;

const OnboardingContentGroupTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OnboardingContentGroupTitleDescription = styled.p`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
`;

const OnboardingFieldLabel = styled.div`
  color: var(--greyscale-shade);
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-regular);
  padding: 0 15px;
`;

const OnboardingUserListHeader = styled.div`
  color: var(--greyscale-shade);
  display: grid;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  gap: 20px;
  grid-template-columns: 1fr 1fr 1.5fr 1fr auto;
  text-transform: uppercase;
  width: 100%;
`;

const OnboardingUserListHeaderItem = styled.span``;

const OnboardingFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OnboardingFieldErrorText = styled.div`
  color: var(--primary-oqio-red);
  font-size: 12px;
  line-height: 16px;
  margin-top: 4px;
  padding-left: 15px;
`;

const OnboardingUserListRow = styled.div`
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr 1.5fr 1fr auto;
  padding: 20px;
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid var(--greyscale-dark-white);
  }

  button {
    height: 20px !important;
    margin-top: 10px;
    width: 20px !important;
  }
`;

const OnboardingOrganizationListHeader = styled.div`
  color: var(--greyscale-shade);
  display: grid;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  gap: 20px;
  grid-template-columns: 1fr 1fr 1fr auto;
  text-transform: uppercase;
  width: 100%;
`;

const OnboardingOrganizationListHeaderItem = styled.span``;

const OnboardingOrganizationListRow = styled.div`
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr 1fr auto;
  padding: 20px;
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid var(--greyscale-dark-white);
  }

  button {
    height: 20px !important;
    margin-top: 10px;
    width: 20px !important;
  }
`;

const OnboardingContentTitle = styled.div`
  max-width: 660px;
  padding: 0;
  text-align: center;

  h1,
  h2 {
    margin: 0;
    white-space: break-spaces;
  }
`;

const OnboardingContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 660px;
  text-align: left;
`;

const OnboardingStepWrapper = styled.div`
  align-items: stretch;
  background: var(--greyscale-white);
  border: 1px solid var(--greyscale-dark-white);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: auto;
  justify-content: flex-start;
  max-width: 962px;
  width: 100%;

  &.step-locked {
    background: var(--secondary-whisper);
    border: none;
    margin-bottom: 30%;
  }

  &.step-users {
    max-width: 1164px;
    padding-bottom: 20px;
  }
`;

const OnboardingOverviewStageItem = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const OnboardingOverviewStageItemHeader = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const OnboardingOverviewStageTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
`;

const OnboardingOverviewStageNumber = styled.div`
  align-items: center;
  background: ${(props) => (props.active ? 'var(--primary-oqio-blue)' : 'none')};
  border: ${(props) => (props.active ? 'none' : '1.5px solid var(--greyscale-shade)')};
  border-radius: 50%;
  box-sizing: border-box;
  color: ${(props) => (props.active ? 'var(--greyscale-white)' : 'var(--greyscale-shade)')};
  display: flex;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  height: 32px;
  justify-content: center;
  line-height: var(--font-line-height-regular);
  width: 32px;
`;

const OnboardingOverviewStageContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const OnboardingOverviewStageTitle = styled(Heading3)`
  color: ${(props) => (props.active ? 'var(--greyscale-blackish)' : 'var(--greyscale-shade)')};
  margin: 0;
`;

const OnboardingOverviewStageDescription = styled.p`
  color: ${(props) => (props.active ? 'var(--greyscale-blackish)' : 'var(--greyscale-shade)')};
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 550px;
`;

const OnboardingOverviewStageAction = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
`;

const OnboardingOverviewStageCompletedLabel = styled.div`
  align-items: center;
  color: var(--greyscale-blackish);
  display: flex;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  gap: 8px;
  line-height: var(--font-line-height-regular);

  svg {
    path {
      stroke: var(--primary-oqio-blue);
    }
  }
`;

const OnboardingOverviewStageConnectionLineWrapper = styled.div`
  display: flex;
  flex: 0 0 32px;
  height: 60px;
  justify-content: center;
  margin-top: 6px;
  padding: 0 15px;
`;

const OnboardingOverviewStageConnectionLine = styled.div`
  background: ${(props) => (props.completed ? 'var(--primary-oqio-blue)' : 'var(--greyscale-light-grey)')};
  height: 100%;
  width: 2px;
`;

const OnboardingDataSourceCardsGroup = styled(RadioButton.Group)`
  display: grid;
  gap: 40px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;

  @media ${windowMaxWidth.splitScreen} {
    gap: 24px;
    grid-template-columns: 1fr;
  }
`;

const OnboardingDataSourceCard = styled(RadioButton)`
  &.ant-radio-wrapper {
    align-items: flex-start;
    background-color: var(--greyscale-white);
    border: 2px solid var(--greyscale-dark-white);
    border-radius: 8px;
    display: flex;
    flex: 1;
    gap: 0;
    margin: 0;
    max-width: none;
    min-height: 286px;
    padding: 32px;
    width: 100%;
  }

  &.ant-radio-wrapper .ant-radio {
    display: none;
  }

  &.ant-radio-wrapper-checked {
    background-color: var(--secondary-whisper);
    border-color: var(--primary-oqio-blue);
  }
`;

const OnboardingDataSourceCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  h3 {
    margin-bottom: 8px;
  }
`;

const OnboardingDataSourceCardBadge = styled.span`
  align-self: flex-start;
  background-color: ${({ $alternative }) => ($alternative ? 'var(--greyscale-concrete)' : 'var(--primary-oqio-blue)')};
  border-radius: var(--border-radius-medium);
  color: var(--greyscale-white);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-small);
  max-width: max-content;
  padding: 2px 10px;
`;

const OnboardingDataSourceCardDescription = styled.p`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
`;

const OnboardingContentGroupText = styled.p`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 660px;
  width: 100%;

  a {
    color: var(--primary-oqio-blue);
    font-weight: var(--font-weight-medium);

    &:hover {
      color: var(--primary-oqio-blue-hover);
    }
  }

  &:not(:last-of-type) {
    margin-bottom: 20px;
  }
`;

const OnboardingContentGroupLink = styled.a`
  color: var(--primary-oqio-blue);
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-regular);
  margin: 10px 0 0;
  max-width: 660px;
  width: 100%;

  &:hover {
    color: var(--primary-oqio-blue-hover);
  }
`;

const OnboardingImportStepList = styled.div`
  margin: 0;
  padding: 0;
  width: 100%;
`;

const OnboardingImportStepItem = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  gap: 16px;
  padding: 20px 40px;

  &:last-child {
    border-bottom: none;
  }
`;

const OnboardingImportStepNumber = styled.div`
  align-items: center;
  border: 1.5px solid var(--primary-oqio-blue);
  border-radius: 50%;
  box-sizing: border-box;
  color: var(--primary-oqio-blue);
  display: flex;
  font-size: var(--font-size-regular);
  font-weight: 500;
  font-weight: var(--font-weight-medium);
  height: 32px;
  justify-content: center;
  line-height: var(--font-line-height-regular);
  width: 32px;
`;

const OnboardingImportStepContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  margin-top: 3px;
`;

const OnboardingImportStepHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
`;

const OnboardingImportStepTitle = styled(Heading3)`
  margin: 0;
`;

const OnboardingImportStepDescription = styled.div`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  max-width: 550px;
`;

const OnboardingImportStepUploaderWrapper = styled.div`
  height: 70px;
  margin-top: 8px;
  max-width: 100%;
`;

const OnboardingActionsContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 50px;
  justify-content: ${(props) => (props.$centered ? 'center' : 'space-between')};
  margin-bottom: 50px;
  max-width: 962px;
  width: 100%;

  &.step-users {
    max-width: 1164px;
  }

  &.step-initial {
    margin-bottom: 0;
    padding: 20px 40px 40px;
  }
`;

const OnboardingActionsSpacer = styled.div`
  width: 0;
`;

const OnboardingActionsNextWrapper = styled.div`
  align-items: ${(props) => (props.$centered ? 'center' : 'flex-end')};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OnboardingActionsNextHint = styled.p`
  color: var(--greyscale-shade);
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-regular);
  margin: 0;
`;

const OnboardingDocumentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  > div {
    margin-bottom: 10px;
  }

  ul {
    margin-bottom: 10px;
  }
`;

const OnboardingContentGroupDescription = styled.div`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin-bottom: 10px;
  max-width: 70%;

  @media (${windowMaxWidth.splitScreen}) {
    max-width: 100%;
  }
`;

const OnboardingNotificationRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 150px;
  justify-content: flex-start;
  width: 100%;
`;

const OnboardingNotificationRowInfo = styled.div`
  max-width: 350px;
  min-width: 350px;

  h3 {
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
  }
`;

const OnboardingNotificationRowActions = styled.div`
  display: flex;
  gap: 24px;
`;

const OnboardingNotificationRowHint = styled(OnboardingContentGroupText)`
  color: var(--greyscale-shade);
`;

const OnboardingNotificationEmptyState = styled.div`
  color: var(--greyscale-shade);
  padding: 24px;
  text-align: center;
`;

const OnboardingInsuranceInfoIcon = styled.span`
  align-items: center;
  color: var(--primary-oqio-blue);
  display: flex;

  svg {
    circle {
      stroke: var(--primary-oqio-blue);
    }

    path:first-of-type {
      fill: var(--primary-oqio-blue);
    }

    path:last-of-type {
      stroke: var(--primary-oqio-blue);
    }
  }
`;

const OnboardingPortalInfo = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 1 0 320px;
  flex-direction: column;
  text-align: center;
`;

const OnboardingPortalInfoTitle = styled.p`
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-regular);
  margin: 0;
`;

const OnboardingPortalInfoContent = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;

  button {
    height: 30px !important;
    width: 30px !important;
  }
`;

const OnboardingPortalInfoText = styled.p`
  line-height: var(--font-line-height-regular);
  margin: 0;
  white-space: nowrap;
`;

const OnboardingApiInfoText = styled.p`
  color: var(--greyscale-shade);
  margin-bottom: 20px;
  max-width: 550px;

  &:last-of-type {
    margin-bottom: 10px;
  }
`;

const OnboardingApiLinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  a {
    color: var(--primary-oqio-blue);
    font-weight: var(--font-weight-medium);
    line-height: var(--font-line-height-regular);
  }
`;

export {
  OnboardingNavbarWrapper,
  OnboardingProgressStepsWrapper,
  OnboardingHeaderGroup,
  OnboardingPageWrapper,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentGroupTitle,
  OnboardingContentGroupTitleDescription,
  OnboardingFieldWrapper,
  OnboardingFieldErrorText,
  OnboardingUserListHeader,
  OnboardingUserListHeaderItem,
  OnboardingUserListRow,
  OnboardingOrganizationListHeader,
  OnboardingOrganizationListHeaderItem,
  OnboardingOrganizationListRow,
  OnboardingContentTitle,
  OnboardingContentInfo,
  OnboardingStepWrapper,
  OnboardingOverviewStageItem,
  OnboardingOverviewStageItemHeader,
  OnboardingOverviewStageTitleWrapper,
  OnboardingOverviewStageNumber,
  OnboardingOverviewStageContent,
  OnboardingOverviewStageTitle,
  OnboardingOverviewStageDescription,
  OnboardingOverviewStageAction,
  OnboardingOverviewStageCompletedLabel,
  OnboardingOverviewStageConnectionLineWrapper,
  OnboardingOverviewStageConnectionLine,
  OnboardingDataSourceCardsGroup,
  OnboardingDataSourceCard,
  OnboardingDataSourceCardContent,
  OnboardingDataSourceCardBadge,
  OnboardingDataSourceCardDescription,
  OnboardingContentGroupText,
  OnboardingContentGroupLink,
  OnboardingImportStepList,
  OnboardingImportStepItem,
  OnboardingImportStepNumber,
  OnboardingImportStepContent,
  OnboardingImportStepHeader,
  OnboardingImportStepTitle,
  OnboardingImportStepDescription,
  OnboardingImportStepUploaderWrapper,
  OnboardingActionsContainer,
  OnboardingActionsSpacer,
  OnboardingActionsNextWrapper,
  OnboardingActionsNextHint,
  OnboardingDocumentSection,
  OnboardingPortalInfo,
  OnboardingPortalInfoTitle,
  OnboardingPortalInfoContent,
  OnboardingPortalInfoText,
  OnboardingContentGroupDescription,
  OnboardingNotificationRow,
  OnboardingNotificationRowInfo,
  OnboardingNotificationRowActions,
  OnboardingNotificationRowHint,
  OnboardingNotificationEmptyState,
  OnboardingInsuranceInfoIcon,
  OnboardingApiInfoText,
  OnboardingApiLinksWrapper,
  OnboardingFieldLabel
};
